import { Location } from '@/api/types/Location';
import ConfirmDialog from '@/components/utils/ConfirmDialog';
import { LocationContext } from '@/contexts/LocationContext';
import { Delete } from '@mui/icons-material';
import { forwardRef, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Tooltip } from '@mui/material';

type DeleteLocationProps = {
  location: Location;
  onClose: () => void;
};

const DeleteLocation = forwardRef<HTMLButtonElement, DeleteLocationProps>(
  ({ location, onClose }, ref) => {
    const { t } = useTranslation();

    const [sending, setSending] = useState(false);
    const { removeLocation } = useContext(LocationContext);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const closeConfirmDialog = () => {
      setConfirmOpen(false);
    };

    const onRemove = async () => {
      setSending(true);
      if (location) {
        await removeLocation(location.locationId);
      }
      onClose();
      setSending(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setConfirmOpen(true);
      }
    };

    return (
      <>
        <Tooltip title={t('Delete location')} arrow>
          <IconButton
            ref={ref}
            onClick={() => setConfirmOpen(true)}
            onKeyDown={handleKeyDown}
            aria-label={t('Delete location') || ''}
            sx={{
              cursor: 'pointer',
            }}
          >
            <Delete />
          </IconButton>
        </Tooltip>
        <ConfirmDialog
          bodyText={t(
            'Are you sure you want to delete this Location. This action will remove the location from all activities.',
          )}
          onClose={closeConfirmDialog}
          onConfirm={onRemove}
          title={t('Delete location')}
          open={confirmOpen}
          yesNo
        />
      </>
    );
  },
);

DeleteLocation.displayName = 'DeleteLocation';

export default DeleteLocation;
